/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Palette, Sun, Moon, Monitor, RefreshCw } from 'lucide-react';
import { useTheme } from '@/lib/theme';
import { ThemeTutorial } from '@/components/ui';

export default function ThemeTutorialPage() {
  const navigate = useNavigate();
  const { theme, setTheme, isDark, resetTutorial } = useTheme();
  const [showTutorial, setShowTutorial] = useState(false);

  const handleShowTutorial = () => {
    resetTutorial?.();
    setShowTutorial(true);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/superadmin')}
          className="p-2 rounded-lg border border-border hover:bg-hover transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Palette className="w-6 h-6" /> Theme Tutorial Demo
          </h1>
          <p className="text-sm text-muted">Preview and test the theme tutorial component</p>
        </div>
      </div>

      {/* Current Theme Status */}
      <div className="bg-surface border border-border rounded-xl p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Current Theme Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-background rounded-lg p-4 border border-border-subtle">
            <div className="text-xs text-muted uppercase tracking-wider mb-1">Theme Setting</div>
            <div className="text-lg font-mono font-semibold text-foreground capitalize">{theme}</div>
          </div>
          <div className="bg-background rounded-lg p-4 border border-border-subtle">
            <div className="text-xs text-muted uppercase tracking-wider mb-1">Rendered As</div>
            <div className="text-lg font-mono font-semibold text-foreground">{isDark ? 'Dark' : 'Light'}</div>
          </div>
          <div className="bg-background rounded-lg p-4 border border-border-subtle">
            <div className="text-xs text-muted uppercase tracking-wider mb-1">System Preference</div>
            <div className="text-lg font-mono font-semibold text-foreground">
              {typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'Dark' : 'Light'}
            </div>
          </div>
        </div>
      </div>

      {/* Theme Switcher */}
      <div className="bg-surface border border-border rounded-xl p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Theme Switcher</h2>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setTheme('light')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
              theme === 'light'
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border hover:border-primary/50 text-foreground'
            }`}
          >
            <Sun className="w-4 h-4" />
            Light
          </button>
          <button
            onClick={() => setTheme('dark')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
              theme === 'dark'
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border hover:border-primary/50 text-foreground'
            }`}
          >
            <Moon className="w-4 h-4" />
            Dark
          </button>
          <button
            onClick={() => setTheme('system')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
              theme === 'system'
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border hover:border-primary/50 text-foreground'
            }`}
          >
            <Monitor className="w-4 h-4" />
            System
          </button>
        </div>
      </div>

      {/* Tutorial Demo */}
      <div className="bg-surface border border-border rounded-xl p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Theme Tutorial Component</h2>
        <p className="text-sm text-muted mb-4">
          The ThemeTutorial component is a guided modal that helps users understand and configure their theme preferences.
          It normally appears automatically on first visit.
        </p>

        <div className="relative inline-block">
          <button
            onClick={handleShowTutorial}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Show Tutorial
          </button>

          {showTutorial && (
            <ThemeTutorial position="right" showDelay={0} />
          )}
        </div>
      </div>

      {/* Color Palette Preview */}
      <div className="bg-surface border border-border rounded-xl p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Color Palette Preview</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <ColorSwatch name="Primary" className="bg-primary" />
          <ColorSwatch name="Background" className="bg-background border border-border" />
          <ColorSwatch name="Surface" className="bg-surface border border-border" />
          <ColorSwatch name="Hover" className="bg-hover border border-border" />
          <ColorSwatch name="Success" className="bg-emerald-500" />
          <ColorSwatch name="Warning" className="bg-amber-500" />
          <ColorSwatch name="Error" className="bg-red-500" />
          <ColorSwatch name="Info" className="bg-blue-500" />
        </div>
      </div>

      {/* Text Samples */}
      <div className="bg-surface border border-border rounded-xl p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Text Samples</h2>
        <div className="space-y-3">
          <p className="text-foreground">This is primary foreground text</p>
          <p className="text-muted">This is muted/secondary text</p>
          <p className="text-primary">This is primary accent text</p>
          <p className="text-emerald-500">This is success text</p>
          <p className="text-red-500">This is error text</p>
        </div>
      </div>
    </div>
  );
}

// Color Swatch Component
function ColorSwatch({ name, className }: { name: string; className: string }) {
  return (
    <div className="text-center">
      <div className={`w-full h-16 rounded-lg mb-2 ${className}`} />
      <span className="text-xs text-muted">{name}</span>
    </div>
  );
}
